import React, { useState, useEffect } from 'react';
import { DocusealForm } from '@docuseal/react';
import { message } from 'antd';
import { initializeDocuSealSubmission, checkDocuSealSigningComplete } from '../../utils/apiClient';
import './DocumentSigning.css';

const DocumentSigning = ({ 
  onNext, 
  isLoading = false, 
  setIsLoading = () => {}, 
  setDocumentSigningCompleted = () => {} 
}) => {
  const [currentDocument, setCurrentDocument] = useState(0);
  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: 'NDA',
      // title: 'Non-Disclosure Agreement',
      formKey: 'nda_vcai',
      completed: false,
      embedSrc: null,
      submissionId: null,
      status: 'pending'
    },
    {
      id: 2,
      name: 'BAA',
      // title: 'Business Associate Agreement',
      formKey: 'baa_vcai',
      completed: false,
      embedSrc: null,
      submissionId: null,
      status: 'pending'
    }
  ]);

  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [formCompleted, setFormCompleted] = useState(false);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [documentAlreadyCompleted, setDocumentAlreadyCompleted] = useState(false);

  // Reset all documents to incomplete state
  const resetDocumentCompletion = () => {
    console.log('🔄 Resetting all documents to incomplete state');
    const resetDocuments = documents.map(doc => ({
      ...doc,
      completed: false,
      status: 'pending'
    }));
    setDocuments(resetDocuments);
  };

  // Re-check completion status from API
  const refreshCompletionStatus = async () => {
    console.log('🔄 Refreshing completion status from API...');
    const updatedDocuments = [...documents];
    
    for (let i = 0; i < updatedDocuments.length; i++) {
      try {
        const result = await checkDocuSealSigningComplete({
          formKey: updatedDocuments[i].formKey
        });
        
        if (result.success && result.data && result.data.status === 'completed') {
          console.log(`📄 Document ${updatedDocuments[i].name} API says completed, but requiring user to actually sign it`);
          // Don't automatically mark as completed - require user to actually complete the signing process
          updatedDocuments[i] = {
            ...updatedDocuments[i],
            completed: false,
            status: 'pending'
          };
        } else {
          console.log(`📄 Document ${updatedDocuments[i].name} is not completed according to API`);
          updatedDocuments[i] = {
            ...updatedDocuments[i],
            completed: false,
            status: 'pending'
          };
        }
      } catch (error) {
        console.log(`📄 Could not check completion status for ${updatedDocuments[i].name}:`, error);
        // Keep current status if we can't check
      }
    }
    
    setDocuments(updatedDocuments);
    console.log('📄 Updated documents after refresh:', updatedDocuments);
  };

  // Mark NDA as completed (since it was actually signed)
  const markNDAComplete = () => {
    console.log('📄 Manually marking NDA as completed since it was actually signed');
    const updatedDocuments = [...documents];
    updatedDocuments[0] = {
      ...updatedDocuments[0],
      completed: true,
      status: 'completed'
    };
    setDocuments(updatedDocuments);
  };

  // Use either the prop isLoading or local loading state
  const currentIsLoading = isLoading !== false ? isLoading : localIsLoading;
  const currentSetIsLoading = setIsLoading !== (() => {}) ? setIsLoading : setLocalIsLoading;

  // Check completion status from API on component mount
  useEffect(() => {
    const checkInitialCompletionStatus = async () => {
      console.log('🔍 Checking initial completion status for all documents...');
      const updatedDocuments = [...documents];
      
      for (let i = 0; i < updatedDocuments.length; i++) {
        try {
          const result = await checkDocuSealSigningComplete({
            formKey: updatedDocuments[i].formKey
          });
          
          if (result.success && result.data && result.data.status === 'completed') {
            console.log(`📄 Document ${updatedDocuments[i].name} API says completed, but checking if user actually signed it`);
            // Don't automatically mark as completed - require user to actually complete the signing process
            // The API might report completed even if user hasn't signed yet
            console.log(`📄 Keeping ${updatedDocuments[i].name} as incomplete until user signs it`);
            updatedDocuments[i] = {
              ...updatedDocuments[i],
              completed: false,
              status: 'pending'
            };
          } else {
            console.log(`📄 Document ${updatedDocuments[i].name} is not completed according to API`);
            updatedDocuments[i] = {
              ...updatedDocuments[i],
              completed: false,
              status: 'pending'
            };
          }
        } catch (error) {
          console.log(`📄 Could not check completion status for ${updatedDocuments[i].name}:`, error);
          // Keep as incomplete if we can't check
          updatedDocuments[i] = {
            ...updatedDocuments[i],
            completed: false,
            status: 'pending'
          };
        }
      }
      
      setDocuments(updatedDocuments);
      console.log('📄 Updated documents after API check:', updatedDocuments);
    };

    checkInitialCompletionStatus();
  }, []); // Run once on component mount



  // Load document submission when document is selected
  useEffect(() => {
    if (documents[currentDocument] && !documents[currentDocument].embedSrc) {
      createSubmission(documents[currentDocument].formKey);
    } else if (documents[currentDocument] && documents[currentDocument].embedSrc) {
      setSubmission({
        embedSrc: documents[currentDocument].embedSrc,
        submissionId: documents[currentDocument].submissionId
      });
    }
  }, [currentDocument]);

  // Check if document is already completed based on submission data
  useEffect(() => {
    if (submission && submission.embedSrc) {
      // Check if there's completion data in the submission
      // This handles cases where the form was completed previously
      const checkCompletionFromSubmission = async () => {
        try {
          // If we have a submissionId, we can check completion status
          if (submission.submissionId) {
            const result = await checkDocuSealSigningComplete({
              formKey: documents[currentDocument]?.formKey
            });
            
            if (result.success && result.data && result.data.status === 'completed') {
              console.log('📄 Document shows as completed in API, but not marking as completed automatically');
              console.log('📄 User must complete the signing process manually');
              // Don't automatically mark as completed - wait for user to actually complete signing
              // setFormCompleted(true);
              
              // Mark current document as completed
              // const updatedDocuments = [...documents];
              // updatedDocuments[currentDocument] = {
              //   ...updatedDocuments[currentDocument],
              //   completed: true,
              //   status: 'completed'
              // };
              // setDocuments(updatedDocuments);
            }
          }
        } catch (error) {
          console.log('Could not check completion status:', error);
        }
      };
      
      // Check completion status after a short delay
      setTimeout(checkCompletionFromSubmission, 1000);
    }
  }, [submission, currentDocument]);

  // Monitor DocuSeal form loading
  useEffect(() => {
    if (submission && submission.embedSrc) {
      console.log('📄 DocuSeal form URL:', submission.embedSrc);
      
      // Force iframe resize after a short delay to ensure proper loading
      const timer = setTimeout(() => {
        const iframes = document.querySelectorAll('iframe[src*="docuseal.com"]');
        console.log('🔍 Found iframes:', iframes.length);
        
        iframes.forEach((iframe, index) => {
          console.log(`🔧 Resizing iframe ${index}:`, iframe.src);
          
          // Set proper dimensions
          iframe.style.height = '100%';
          iframe.style.minHeight = '0';
          iframe.style.width = '100%';
          iframe.style.overflow = 'auto';
          iframe.style.overflowY = 'scroll';
          iframe.style.overflowX = 'hidden';
          iframe.style.position = 'relative';
          iframe.style.zIndex = '1';
          iframe.style.display = 'block';
          iframe.style.visibility = 'visible';
          iframe.style.maxHeight = 'none';
          
          // Ensure iframe content is accessible
          iframe.style.pointerEvents = 'auto';
          
          // Set scrolling attribute
          iframe.setAttribute('scrolling', 'auto');
          
          // Add event listeners to monitor iframe loading
          iframe.onload = () => {
            console.log(`✅ Iframe ${index} loaded successfully`);
            // Force a resize after iframe loads
            setTimeout(() => {
              iframe.style.height = '100%';
              iframe.style.minHeight = '0';
              iframe.style.overflow = 'auto';
              iframe.style.overflowY = 'scroll';
              iframe.style.overflowX = 'hidden';
              iframe.setAttribute('scrolling', 'auto');
              
              // Try to access iframe content and ensure scrolling
              try {
                if (iframe.contentDocument) {
                  iframe.contentDocument.body.style.overflow = 'auto';
                  iframe.contentDocument.body.style.overflowY = 'scroll';
                  iframe.contentDocument.body.style.height = 'auto';
                  iframe.contentDocument.body.style.minHeight = '100%';
                }
              } catch (e) {
                console.log('🔒 Cannot access iframe content due to CORS:', e.message);
              }
            }, 500);
          };
          
          iframe.onerror = (error) => {
            console.error(`❌ Iframe ${index} failed to load:`, error);
          };
        });
        
        // Also check for any DocuSeal form elements
        const docusealElements = document.querySelectorAll('[data-testid="docuseal-form"], .docuseal-form');
        console.log('🔍 Found DocuSeal elements:', docusealElements.length);
        
        docusealElements.forEach((element, index) => {
          console.log(`🔧 Configuring DocuSeal element ${index}:`, element);
          element.style.height = '100%';
          element.style.minHeight = '0';
          element.style.width = '100%';
          element.style.overflow = 'auto';
          element.style.overflowY = 'scroll';
          element.style.overflowX = 'hidden';
          element.style.position = 'relative';
          element.style.zIndex = '1';
        });
        
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [submission]);

  // Check if document is already submitted
  useEffect(() => {
    if (submission && submission.embedSrc) {
      // Check if the form shows "Form has been submitted already"
      const checkIfSubmitted = () => {
        console.log('🔍 Checking if document is already submitted...');
        const iframes = document.querySelectorAll('iframe[src*="docuseal.com"]');
        console.log('🔍 Found iframes:', iframes.length);
        
        iframes.forEach((iframe, index) => {
          try {
            if (iframe.contentDocument) {
              const submittedText = iframe.contentDocument.body?.textContent;
              console.log(`🔍 Iframe ${index} content preview:`, submittedText?.substring(0, 200));
              
              if (submittedText && submittedText.includes('Form has been submitted already')) {
                console.log('📄 Document shows as submitted, but not marking as completed automatically');
                console.log('📄 User must complete the signing process manually');
                // Don't automatically mark as completed - wait for user to actually complete signing
                // setFormCompleted(true);
                // Mark current document as completed
                // const updatedDocuments = [...documents];
                // updatedDocuments[currentDocument] = {
                //   ...updatedDocuments[currentDocument],
                //   completed: true,
                //   status: 'completed'
                // };
                // setDocuments(updatedDocuments);
                // console.log('📄 Updated documents:', updatedDocuments);
                
                // Generate download URL
                const downloadUrl = `https://34.54.79.89.nip.io/customer-ui-ms/api/v1/docuseal/download/${submission.submissionId}`;
                // setDownloadUrl(downloadUrl); // Removed downloadUrl state
              }
            }
          } catch (e) {
            // CORS error, try alternative approach
            console.log('🔒 Cannot access iframe content, using alternative detection');
            
            // Alternative: Check for specific text patterns in the page
            const pageText = document.body.textContent || '';
            // Disable automatic completion detection - only mark complete when user actually completes signing
            console.log('📄 Alternative detection disabled - waiting for user to complete signing manually');
            // Remove automatic completion to prevent premature enabling of Next button
          }
        });
      };
      
      // Check after iframe loads - multiple attempts
      setTimeout(checkIfSubmitted, 1000);
      setTimeout(checkIfSubmitted, 3000);
      setTimeout(checkIfSubmitted, 5000);
      setTimeout(checkIfSubmitted, 8000);
    }
  }, [submission, currentDocument, documents]);

  // Monitor for DocuSeal modals and reduce their size
  useEffect(() => {
    if (submission) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for modals
              const modals = node.querySelectorAll ? node.querySelectorAll('.modal, .popup, [data-testid*="modal"], [data-testid*="popup"]') : [];
              modals.forEach(modal => {
                console.log('🔧 Reducing modal size:', modal);
                modal.style.maxWidth = '400px';
                modal.style.maxHeight = '300px';
                modal.style.width = '90%';
                modal.style.height = 'auto';
                modal.style.minHeight = '200px';
                modal.style.transform = 'scale(0.9)';
                modal.style.transformOrigin = 'center center';
              });

              // Check for input fields
              const inputs = node.querySelectorAll ? node.querySelectorAll('input, textarea, select') : [];
              inputs.forEach(input => {
                console.log('🔧 Reducing input size:', input);
                input.style.fontSize = '14px';
                input.style.padding = '8px 12px';
                input.style.height = '36px';
                input.style.minHeight = '36px';
                input.style.maxHeight = '36px';
                input.style.lineHeight = '1.2';
                input.style.borderRadius = '4px';
                input.style.border = '1px solid #ccc';
                input.style.boxSizing = 'border-box';
              });

              // Check for buttons
              const buttons = node.querySelectorAll ? node.querySelectorAll('button, .btn') : [];
              buttons.forEach(button => {
                console.log('🔧 Reducing button size:', button);
                button.style.fontSize = '14px';
                button.style.padding = '8px 16px';
                button.style.height = '36px';
                button.style.minHeight = '36px';
                button.style.lineHeight = '1.2';
                button.style.borderRadius = '4px';
              });

              // Check for labels
              const labels = node.querySelectorAll ? node.querySelectorAll('label') : [];
              labels.forEach(label => {
                console.log('🔧 Reducing label size:', label);
                label.style.fontSize = '13px';
                label.style.marginBottom = '4px';
                label.style.lineHeight = '1.2';
              });
            }
          });
        });
      });

      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    }
  }, [submission]);

  const createSubmission = async (formKey) => {
    currentSetIsLoading(true);
    setError(null);

    try {
      console.log('Creating submission for:', formKey);

      const result = await initializeDocuSealSubmission({
        formKey
      });

      if (result.success) {
        console.log('Submission created:', result.data);
        
        // Extract customer email from the response
        const email = result.data.customerEmail;
        console.log('📧 Customer email from response:', email);
        setCustomerEmail(email);
        
        // Update the current document with submission data
        const updatedDocuments = [...documents];
        updatedDocuments[currentDocument] = {
          ...updatedDocuments[currentDocument],
          embedSrc: result.data.embedSrc,
          submissionId: result.data.submissionId,
          status: 'active'
        };
        setDocuments(updatedDocuments);
        setSubmission(result.data);
      } else {
        throw new Error(result.error || 'Failed to create submission');
      }
      
    } catch (err) {
      console.error('Error creating submission:', err);
      setError(err.message || 'Failed to create submission. Please try again.');
    } finally {
      currentSetIsLoading(false);
    }
  };

  const handleNext = async () => {
    onNext();
  }

  const completeSigning = async (formKey) => {
    currentSetIsLoading(true);
    setError(null);

    try {
      console.log('🔍 Completing signing for formKey:', formKey);
      console.log('🔍 Current document:', documents[currentDocument]);

      const result = await checkDocuSealSigningComplete({
        formKey
      });

      if (result.success) {
        console.log('Completion response:', result.data);
        
        // Check if document is already completed
        if (result.data && result.data.status === 'completed') {
          console.log('📄 Document shows as completed in API, but not marking as completed automatically');
          setDocumentAlreadyCompleted(true);
          // Don't automatically mark as completed - wait for user to actually complete signing
          // setFormCompleted(true);
          
          // Mark current document as completed
          // const updatedDocuments = [...documents];
          // updatedDocuments[currentDocument] = {
          //   ...updatedDocuments[currentDocument],
          //   completed: true,
          //   status: 'completed'
          // };
          // setDocuments(updatedDocuments);
        } else {
          console.log('📄 Document not completed yet - user must complete signing process');
          // Don't automatically mark as completed - wait for user to actually complete signing
          // const updatedDocuments = [...documents];
          // updatedDocuments[currentDocument] = {
          //   ...updatedDocuments[currentDocument],
          //   completed: true,
          //   status: 'completed'
          // };
          // setDocuments(updatedDocuments);
          // setFormCompleted(true);
        }
        
        // Move to next document if available
        if (currentDocument < documents.length - 1) {
          setTimeout(() => {
            setCurrentDocument(currentDocument + 1);
            setSubmission(null);
            setCustomerEmail(null); // Reset customer email for next document
            setFormCompleted(false);
          }, 2000); // Wait 2 seconds before moving to next document
        } else {
          console.log('🎉 All documents completed!');
          console.log('📋 Setting documentSigningCompleted to true');
          setDocumentSigningCompleted(true); // Notify parent that all documents are completed
          
          // Show completion message and proceed to next step
          setTimeout(() => {
            console.log('⏰ Timeout completed, calling onNext...');
            if (onNext) {
              console.log('📞 Calling onNext function');
              onNext(); // Call onNext to proceed to the next step
            } else {
              console.log('⚠️ No onNext function provided');
              // If not in onboarding flow, show success message
              alert('✅ All documents have been signed successfully! You can now proceed with your account setup.');
            }
          }, 1000); // Timeout to show completion message
        }
      } else {
        throw new Error(result.error || 'Failed to complete signing');
      }
      
    } catch (err) {
      console.error('Error completing signing:', err);
      setError(err.message || 'Failed to complete signing. Please try again.');
    } finally {
      currentSetIsLoading(false);
    }
  };

  const handleDocumentSelect = (index) => {
    setCurrentDocument(index);
    setError(null);
    setCustomerEmail(null); // Reset customer email when selecting a document
    setFormCompleted(false);
    setDocumentAlreadyCompleted(false);
  };

  const handleDocumentDecline = () => {
    console.log('Document declined by user');
    // Mark current document as declined
    const updatedDocuments = [...documents];
    updatedDocuments[currentDocument] = {
      ...updatedDocuments[currentDocument],
      status: 'declined'
    };
    setDocuments(updatedDocuments);
    
    // Move to next document if available
    if (currentDocument < documents.length - 1) {
      handleDocumentSelect(currentDocument + 1);
    } else {
      // All documents processed
      console.log('All documents processed!');
    }
  };

  // Handle form completion
  const handleFormComplete = (data) => {
    console.log('🎉 DocuSeal form completed with data:', data);
    console.log('📄 Calling completion endpoint...');
    setFormCompleted(true);
    
    // Mark current document as completed immediately
    const updatedDocuments = [...documents];
    updatedDocuments[currentDocument] = {
      ...updatedDocuments[currentDocument],
      completed: true,
      status: 'completed'
    };
    setDocuments(updatedDocuments);
    
    // Call the completion endpoint with the current document's formKey
    const currentFormKey = documents[currentDocument]?.formKey;
    completeSigning(currentFormKey);
    
    // Note: onNext will be called from completeSigning when all documents are done
  };

  const handleManualComplete = () => {
    console.log('🔧 Manual completion triggered');
    completeSigning(documents[currentDocument].formKey);
  };

  const currentDoc = documents[currentDocument];

  return (
    <div className="document-signing-container">
      {/* Header */}
      <div className="document-signing-header">
        <h1>Terms & Services</h1>
      </div>

      <div className="document-signing-content">
        {/* Left Navigation Panel */}
        <div className="document-navigation">
          {documents.map((doc, index) => (
            <button
              key={doc.id}
              className={`document-nav-item ${currentDocument === index ? 'active' : ''} ${doc.completed ? 'completed' : ''} ${doc.status === 'declined' ? 'declined' : ''}`}
              onClick={() => handleDocumentSelect(index)}
            >
              {doc.status === 'declined' && (
                <svg className="decline-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#ff5722"/>
                </svg>
              )}
              {doc.name}
            </button>
          ))}
        </div>

        {/* Right Content Area */}
        <div className="document-content">
          <div className={`custom-document-signing ${currentDocument === 0 ? 'document-1-content' : currentDocument === 1 ? 'document-2-content' : ''}`}>
            <div className="document-header">
              <h2>{documents[currentDocument].title}</h2>
              {/* Removed decline button */}
            </div>
            
            <div className="document-body">
              {error && <div className="alert alert-error">{error}</div>}
              
              {currentIsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading document...</p>
                </div>
              ) : submission ? (
                <div className="docuseal-form-container">
                  {/* <div className="document-instructions">
                    <h4>📝 Please fill out and sign the document below:</h4>
                    <p>Complete all required fields and add your signature to proceed. The form will automatically submit when completed.</p>
                    <p><strong>Debug:</strong> Form URL: {submission.embedSrc}</p>
                    <p><strong>Status:</strong> Form loaded and ready</p>
                  </div> */}
                  
                  <div 
                    style={{ 
                      flex: 1, 
                      height: '100%', 
                      minHeight: '0',
                      position: 'relative',
                      zIndex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: '100%',
                        minHeight: '0',
                        overflow: 'auto',
                        overflowY: 'scroll',
                        overflowX: 'hidden',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                   
                        <DocusealForm
                          src={submission.embedSrc}
                          email={submission.customerEmail || "shreyas+test@voicecare.ai"}
                          onComplete={handleFormComplete}
                          data-with-download-button="true"
                          style={{ 
                            height: '100%', 
                            width: '100%',
                            position: 'relative',
                            zIndex: 1,
                            minHeight: '0'
                          }}
                        />
                   
                      {submission.customerEmail && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '10px', 
                          right: '10px', 
                          background: 'rgba(0,0,0,0.7)', 
                          color: 'white', 
                          padding: '5px 10px', 
                          borderRadius: '4px', 
                          fontSize: '12px',
                          zIndex: 10
                        }}>
                          📧 Using email: {customerEmail}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="error-container">
                  <p>Failed to load document. Please try again.</p>
                  <button 
                    className="btn-retry" 
                    onClick={() => createSubmission(currentDoc.formKey)}
                  >
                    Retry Loading Document
                  </button>
                </div>
              )}
            </div>
            
            <div className="document-footer">
              <div className="footer-actions">
                {formCompleted ? (
                  <div className="completed-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={() => currentDocument < documents.length - 1 && handleDocumentSelect(currentDocument + 1)}
                      disabled={currentDocument >= documents.length - 1}
                    >
                      {currentDocument >= documents.length - 1 ? 'All Documents Complete' : 'Next Document'}
                    </button>
                  </div>
                ) : (
                  <div className="footer-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleManualComplete}
                      disabled={currentIsLoading}
                    >
                      CONFIRM & SUBMIT
                    </button>
                    {/* Show completion status */}
                    {submission && submission.embedSrc && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                        Document loaded. Complete the signing process within the document above.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Action Buttons - Only show on document 2 (BAA) */}
        {currentDocument === 1 && (
          <div className="step-actions">
 
            <button
              className="btn-next"
              onClick={handleNext}
            >
              {"Next"}
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default DocumentSigning; 